import { Delta } from 'quill/core';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated';
import { miscToasts, postToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../../utils/router/useNavigate';
import postPath from '../../utils/url/postPath';

const UPDATE_POST_CONTENT = gql(/** @complexity 2 */ `
  mutation updatePostContent(
    $id: ID!
    $delta: Json!
    $version: Int!
    $checksum: Int!
  ) {
    post: updatePostContent(
      id: $id
      delta: $delta
      version: $version
      checksum: $checksum
    ) {
      id
    }
  }
`);

const POST_CONTENT_QUERY = gql(/** @complexity 10 */ `
  query PostHistoryContent($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      content(isLatest: true) {
        id
        delta
        version
      }
      ...postPath_post
      historyRange(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        snapshot
      }
    }
  }
`);

interface RestoreVersionOptions {
  postId: string;
  versionTo: number;
}

const useRestoreVersion = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loadPoastContent] = useLazyQuery(POST_CONTENT_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [updatePostContent] = useMutation(UPDATE_POST_CONTENT);

  async function restore({ postId, versionTo }: RestoreVersionOptions) {
    try {
      const { data } = await loadPoastContent({
        variables: { postId, versionFrom: versionTo, versionTo: versionTo + 1 },
      });
      const post = data?.post;
      if (!post?.historyRange) return;

      const currentDelta = new Delta(post.content.delta);
      const targetDelta = new Delta(post.historyRange.snapshot);

      const diff = currentDelta.diff(targetDelta);

      await updatePostContent({
        variables: {
          id: postId,
          delta: JSON.stringify(diff),
          version: post.content.version,
          checksum: currentDelta.length(),
        },
      });
      navigate(postPath(post));
      dispatch(UIActions.pushToast(postToasts.postRestored));
    } catch (e) {
      dispatch(UIActions.pushToast(miscToasts.genericError));
      throw e;
    }
  }

  return restore;
};

export default useRestoreVersion;
